<template>
  <div class="w-100 m-auto shipments-list staff-users">
    <div class="staff-users__header">
      <h3 class="m-0">Staff users</h3>
      <div class="staff-users__actions">
        <button class="btn btn-green" type="button" @click="openCreateModal">Create</button>
      </div>
    </div>

    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow staff-users__table"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>No staff users found.</template>
      <template #item="{ column, item }: { column: string; item: StaffUser }">
        <span v-if="column === 'name'">{{ formatName(item) }}</span>
        <span v-if="column === 'email'">{{ item.email }}</span>
        <span v-if="column === 'status'">
          <span :class="['staff-users__status', item.isActive ? 'is-active' : 'is-inactive']">
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </span>
        </span>
        <span v-if="column === 'createdAt'">
          <RowDate :date="item.createdAt" />
        </span>
        <span v-if="column === 'updatedAt'">
          <RowDate :date="item.updatedAt" />
        </span>
        <span v-if="column === 'actions'" class="d-flex justify-content-end">
          <span class="btn-table-action" @click="openEditModal(item)">
            <mdicon name="pencil-outline" class="pointer" size="18" />
          </span>
          <span class="btn-table-action ml-2" @click="openDeleteModal(item)">
            <mdicon name="trash-can-outline" class="pointer text-danger" size="18" />
          </span>
        </span>
      </template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />

    <StaffUserModal :modal-name="STAFF_USER_CREATE" mode="create" @saved="handleCreated" />
    <StaffUserModal :modal-name="STAFF_USER_EDIT" mode="edit" @saved="handleCreated" />
  </div>
</template>

<script lang="ts">
import type { StaffUser } from '@/types/Models/StaffUser'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, computed, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import { STAFF_USER_CREATE, STAFF_USER_EDIT } from '@/const/ModalTypes'
import { openModal, registerModals } from '@/services/Modal'
import { makeRequestParams } from '@/services/StaffUsers'
import { useCustomersStore } from '@/store/customers'
import { useStaffUsersStore } from '@/store/staffUsers'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import StaffUserModal from '@/views/Modals/StaffUser/StaffUserModal.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'StaffUsersIndex',
  components: { BSpinner, EasyDataTable, RowDate, StaffUserModal, TablePagination },
  setup() {
    const staffUsersStore = useStaffUsersStore()
    const customersStore = useCustomersStore()
    const dataTable = ref()

    const loading: ComputedRef<boolean> = computed(() => staffUsersStore.loading)
    const items: ComputedRef<StaffUser[]> = computed(() => staffUsersStore.users)
    const serverItemsLength: ComputedRef<number> = computed(() => staffUsersStore.count)
    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 25,
    })

    const headers: Header[] = [
      { text: 'Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Status', value: 'status' },
      { text: 'Created', value: 'createdAt' },
      { text: 'Updated', value: 'updatedAt' },
      { text: '', value: 'actions' },
    ]

    const formatName = (user: StaffUser) => {
      const pieces = [user.firstName, user.lastName].filter(Boolean)
      return pieces.length ? pieces.join(' ') : '-'
    }

    const loadFromServer = () => {
      staffUsersStore.fetchStaffUsers(makeRequestParams({ serverOptions }))
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    const openCreateModal = () => {
      openModal(STAFF_USER_CREATE)
    }

    const openEditModal = (user: StaffUser) => {
      openModal(STAFF_USER_EDIT, { user })
    }

    const openDeleteModal = (user: StaffUser) => {
      openModal('confirmWrap', {
        title: 'Delete staff user',
        message: `Are you sure you want to delete ${formatName(user)}?`,
        okButtonText: 'Delete',
        onConfirm: () => handleDelete(user.id),
      })
    }

    const handleDelete = (id: string) => {
      staffUsersStore.deleteStaffUser(id).then((deleted) => {
        if (deleted !== false) {
          loadFromServer()
        }
      })
    }

    const handleCreated = () => {
      loadFromServer()
    }

    onMounted(() => {
      registerModals(STAFF_USER_CREATE)
      registerModals(STAFF_USER_EDIT)
      customersStore.fetchAll({ orderBy: 'name', ascending: 1, active: 1 })
      loadFromServer()
    })

    watch(serverOptions, loadFromServer, { deep: true })

    return {
      dataTable,
      loading,
      items,
      serverItemsLength,
      serverOptions,
      headers,
      formatName,
      updateServerOptions,
      openCreateModal,
      openEditModal,
      openDeleteModal,
      handleCreated,
      STAFF_USER_CREATE,
      STAFF_USER_EDIT,
    }
  },
})
</script>

<style scoped lang="scss">
.staff-users__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.staff-users__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.staff-users__status {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.staff-users__status.is-active {
  color: #0f5132;
  background: #d1e7dd;
}

.staff-users__status.is-inactive {
  color: #664d03;
  background: #fff3cd;
}

</style>
